/**
 * @author NikolaV
 */
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { Ajax } from '@shared/Ajax';
import { Notify } from '@shared/ui-native/Notify';
import { store } from '../store/rootStore';
import { LStorage } from '@shared/Storage';

//@ts-ignore
const removeDiacritics = require('diacritics').remove;

export class TripsStore implements ICrudStore<ITruckTrip> {
    @observable items: ITruckTrip[] = [];
    @observable isDoingAsyncWork: boolean = false;
    @observable noOvernightTrips: boolean = false;

    // filter
    @observable page: number = 1;
    @observable perPage: number = 25;
    @observable totalPages: number = 1;
    @observable totalCount: number = 0;
    @observable wholeTableSelected: boolean = false;
    @observable totalStats = {
        totalPieces: null,
        avgPieces: null,
        totalDistance: null,
        avgDistance: null,
        totalTripTime: null,
        avgTripTime: null,
        totalM3: null,
        avgM3: null,
        totalTons: null,
        avgTons: null,
    };

    @computed
    get selectedItems() {
        return this.items.filter(t => t.selected);
    }

    constructor() {
        makeObservable(this);
        reaction(
            () => ({
                page: this.page,
                perPage: this.perPage,
            }),
            () => {
                this.fetch();
            }
        );
    }

    fetchSingleTrip = tripId => {
        this.isDoingAsyncWork = true;

        if (!tripId) {
            this.isDoingAsyncWork = false;
            return;
        }

        return Ajax({
            url: `/project_vehicle_trips/${tripId}`,
            method: 'GET',
            data: {
                projectId: kuura.getProjectId(),
                hasTripImages: true,
            },
        })
            .fail(Notify.error)
            .always(() => (this.isDoingAsyncWork = false));
    };

    fetch = (fetchStats = true) => {
        this.isDoingAsyncWork = true;
        const apiFilter = store.tripsFilter.toApiFilter();

        if (
            !apiFilter.materialIds.length ||
            !apiFilter.vehicleIds.length ||
            !apiFilter.taskIds.length ||
            !apiFilter.loadAreasIds.length ||
            !apiFilter.unloadAreasIds.length ||
            !apiFilter.gpsLoadAreasIds.length ||
            !apiFilter.gpsUnloadAreasIds.length
        ) {
            this.items = [];
            this.totalPages = 0;
            this.totalCount = 0;
            this.isDoingAsyncWork = false;
            return;
        }

        const filter = {
            ...apiFilter,
            materialIds: store.tripsFilter.isAllIdsActiveForFilter('materials') ? null : apiFilter.materialIds,
            vehicleIds: store.tripsFilter.isAllIdsActiveForFilter('vehicles') ? null : apiFilter.vehicleIds,
            taskIds: store.tripsFilter.isAllIdsActiveForFilter('tasks') ? null : apiFilter.taskIds,
            loadAreasIds: store.tripsFilter.isAllIdsActiveForFilter('loadAreas') ? null : apiFilter.loadAreasIds,
            unloadAreasIds: store.tripsFilter.isAllIdsActiveForFilter('unloadAreas') ? null : apiFilter.unloadAreasIds,
            gpsLoadAreasIds: store.tripsFilter.isAllIdsActiveForFilter('gpsLoadAreas') ? null : apiFilter.gpsLoadAreasIds,
            gpsUnloadAreasIds: store.tripsFilter.isAllIdsActiveForFilter('gpsUnloadAreas') ? null : apiFilter.gpsUnloadAreasIds,
        };

        const ajaxData = {
            page: this.page,
            perPage: this.perPage,
            excludeAreas: true,
            projectId: kuura.getProjectId(),
            hasTripImages: true,
            noOvernightTrips: this.noOvernightTrips,
            ...filter,
        };

        if (fetchStats) {
            Ajax({
                url: `trucks/stats/total.json`,
                data: ajaxData,
            }).then(resp => {
                this.totalStats = resp.stats;
            });
        }

        return Ajax({
            url: 'project_vehicle_trips.json',
            data: ajaxData,
        })
            .done(
                action('replace all trips', (resp: any) => {
                    const items: ITruckTrip[] = resp.task_list || resp.all_tasks_list;
                    items.reverse().forEach((trip, i) => {
                        trip.selected = false;
                        trip.t_time = (trip.endDate - trip.startDate) / 1000;
                        trip.fullRowString = removeDiacritics(
                            trip.material + ' ' + trip.vehicleName + ' ' + (trip.task && trip.task.name) + ' ' + trip.endingAreaName + ' ' + trip.startingAreaName
                        ).toLowerCase();
                    });
                    this.items = items.reverse();
                    this.totalPages = Math.ceil(resp.total_count / resp.per_page);
                    this.totalCount = resp.total_count;
                })
            )
            .fail(Notify.error)
            .always(() => (this.isDoingAsyncWork = false));
    };

    create = (trip: any) => {
        if (trip.loadAreaId == -1) trip.loadAreaId = null;
        if (trip.unloadAreaId == -1) trip.unloadAreaId = null;

        this.isDoingAsyncWork = true;
        return Ajax({
            url: 'add_manual_vehicle_task.json',
            method: 'post',
            processData: false,
            data: {
                ...trip,
                projectId: kuura.getProjectId().toString(),
            },
        })
            .done(Notify.success)
            .fail(Notify.error)
            .always(() => (this.isDoingAsyncWork = false));
    };

    bulkUpdate = (ids: number[], trip: any) => {
        if (!trip.loadAreaId) trip.loadAreaId = -2;
        if (!trip.unloadAreaId) trip.unloadAreaId = -2;
        if (trip.loadAreaId == -1) trip.loadAreaId = null;
        if (trip.unloadAreaId == -1) trip.unloadAreaId = null;

        const apiFilter = store.tripsFilter.toApiFilter();

        this.isDoingAsyncWork = true;
        return Ajax({
            url: 'bulk_edit_vehicle_trip.json',
            method: 'post',
            processData: false,
            data: {
                ...trip,
                ids: ids.join(','), // todo, toString is temp hack
                projectId: kuura.getProjectId().toString(), // todo, toString is temp hack
                allSelected: this.wholeTableSelected,
                ...apiFilter,
            },
        })
            .done(Notify.success)
            .fail(Notify.error)
            .always(() => (this.isDoingAsyncWork = false));
    };

    remove = (itemId: number) => {
        if (!confirm(kuura.getMessage('general.confirm'))) return false;
        if (!$.isNumeric(itemId)) return false;

        return Ajax({
            url: 'delete_vehicle_trip.json',
            data: {
                taskId: itemId,
                projectId: kuura.getProjectId(),
            },
        })
            .done(() => {
                this.fetch();
                store.vehicles.fetchStats();
            })
            .fail(Notify.error);
    };

    getStats() {
        const totalTrips = this.items.length;
        let totalVolumeM3 = 0;
        let totalVolumeTons = 0;
        let totalVolumePieces = 0;
        let totalDistance = 0;
        let totalTime = 0;

        for (const { volume, t_time, distance, volumeUnit } of this.items) {
            if ($.isNumeric(volume)) {
                if (volumeUnit == 1) {
                    totalVolumeM3 += volume;
                } else if (volumeUnit == 2) {
                    totalVolumeTons += volume;
                } else {
                    totalVolumePieces += volume;
                }
            }
            if ($.isNumeric(t_time) && t_time > 0) {
                totalTime += t_time;
            }
            if ($.isNumeric(distance)) {
                totalDistance += distance / 1000; //meters to KM
            }
        }

        return {
            avgVolumeM3: totalTrips === 0 ? 0 : totalVolumeM3 / totalTrips,
            avgVolumeTons: totalTrips === 0 ? 0 : totalVolumeTons / totalTrips,
            avgVolumePieces: totalTrips === 0 ? 0 : totalVolumePieces / totalTrips,
            avgDistance: totalTrips === 0 ? 0 : totalDistance / totalTrips,
            avgTime: totalTrips === 0 ? 0 : totalTime / totalTrips,
            totalVolumeM3,
            totalVolumeTons,
            totalVolumePieces,
            totalDistance,
            totalTime,
        };
    }

    downloadTable(extension = 'csv', onlyVisible: boolean = false) {
        const starttime = moment(store.tripsFilter.fromTimestamp).startOf('day').valueOf();
        const endtime = moment(store.tripsFilter.toTimestamp).endOf('day').valueOf();
        const endpoints = {
            csv: 'export_vehicle_trips_csv.json',
            xlsx: 'export_vehicle_trips_xlsx.json',
        };

        let url = `${kuura.getContextPath()}/${endpoints[extension]}?projectId=${kuura.getProjectId()}${store.tripsFilter.urlString}&noOvernightTrips=${this.noOvernightTrips}`;
        console.log('url:', url);

        if (onlyVisible) {
            const filteredItems = this.items;
            if (filteredItems.length >= 1000) {
                alert("Too many tasks, use 'Download whole table' option");
                return;
            }
            const filteredTasks = filteredItems.map(t => t.id);
            url += '&filteredTaskIds=' + filteredTasks.join(',');
        }

        window.open(url);
    }

    getTripImages = (tripId: number) => {
        this.isDoingAsyncWork = true;

        return Ajax({
            url: `project_vehicle_trips/${tripId}/images`,
            data: {
                projectId: kuura.getProjectId(),
            },
        })
            .done(resp => {
                return { attachments: resp.attachments } as { attachments: IImage };
            })
            .fail(Notify.error)
            .always(() => (this.isDoingAsyncWork = false));
    };

    tripContainsText(t: ITruckTrip, filterText: string): boolean {
        if (filterText.length === 0) return true;
        return t.fullRowString.includes(removeDiacritics(filterText).toLowerCase());
    }

    retryTripCalc = (tripId: number) => {
        this.isDoingAsyncWork = true;

        return Ajax({
            url: `/v1/equipment/${tripId}/repair_trip`,
            method: 'POST',
            data: {
                projectId: kuura.getProjectId(),
            },
        })
            .done(Notify.success)
            .fail(Notify.error)
            .always(() => (this.isDoingAsyncWork = false));
    };

    @action
    selectAll() {
        this.wholeTableSelected = false;
        this.items.forEach(i => (i.selected = true));
    }

    @action
    deselectAll() {
        this.wholeTableSelected = false;
        this.items.forEach(i => (i.selected = false));
    }

    @action.bound
    setDates(from: number, to: number) {
        store.tripsFilter.fromTimestamp = from;
        store.tripsFilter.toTimestamp = to;
        this.page = 1;
    }

    toggleNoOvernightTrips() {
        this.noOvernightTrips = !this.noOvernightTrips;
        LStorage.setItem('trucks.noOvernightTrips', this.noOvernightTrips);
        this.fetch();
    }
}
